import { useEffect, useState, useCallback } from "react";

export function useSongFullscreen(cardRef: React.RefObject<HTMLElement | null> | React.RefObject<HTMLDivElement>) {
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isCssFullscreen, setIsCssFullscreen] = useState(false);
  const isFullscreen = isNativeFullscreen || isCssFullscreen;

  // Listen to native fullscreen changes across browsers
  useEffect(() => {
    const handler = () => {
      const isNative = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsNativeFullscreen(isNative);
      if (!isNative) {
        setIsCssFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    document.addEventListener("mozfullscreenchange", handler);
    document.addEventListener("MSFullscreenChange", handler);

    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
      document.removeEventListener("mozfullscreenchange", handler);
      document.removeEventListener("MSFullscreenChange", handler);
    };
  }, []);

  // Lock body scroll when CSS fallback fullscreen is active
  useEffect(() => {
    if (isCssFullscreen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isCssFullscreen]);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      if (isCssFullscreen) {
        setIsCssFullscreen(false);
      }
      try {
        if (
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
        ) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
          } else if ((document as any).mozCancelFullScreen) {
            (document as any).mozCancelFullScreen();
          } else if ((document as any).msExitFullscreen) {
            (document as any).msExitFullscreen();
          }
        }
      } catch (e) {
        console.warn("Exit native fullscreen failed:", e);
      }
    } else {
      const el = cardRef.current as any;
      if (!el) return;
      try {
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          el.mozRequestFullScreen();
        } else if (el.msRequestFullscreen) {
          el.msRequestFullscreen();
        } else {
          setIsCssFullscreen(true);
        }
      } catch (e) {
        // Fallback to CSS fullscreen when native element fullscreen is unsupported/blocked on mobile
        setIsCssFullscreen(true);
      }
    }
  }, [isFullscreen, isCssFullscreen, cardRef]);

  return {
    isFullscreen,
    isNativeFullscreen,
    isCssFullscreen,
    setIsCssFullscreen,
    toggleFullscreen,
  };
}
